import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Loader2, Search, Mail, Pencil, Trash2, Send, Home, CheckCircle2,
    Clock, X, FileText, Users,
} from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_LABELS = {
    not_sent: "Not sent",
    reminder_sent: "Reminder sent",
    waiting_for_records: "Waiting for records",
    records_partially_received: "Records partially received",
    records_received: "Records received",
    ready_for_preparation: "Ready for preparation",
    in_progress: "In progress",
    completed: "Completed",
};

const STATUS_STYLES = {
    not_sent:                   "bg-[#E8E8E4] text-[#5E636E] border-[#D4D4CF]",
    reminder_sent:              "bg-[#E6F0FA] text-[#1E3A5F] border-[#C6DBF0]",
    waiting_for_records:        "bg-[#FFF4D6] text-[#6B4F00] border-[#F0E0A3]",
    records_partially_received: "bg-[#FFF4D6] text-[#6B4F00] border-[#F0E0A3]",
    records_received:           "bg-[#E8F3EC] text-[#1F5235] border-[#C8E0D1]",
    ready_for_preparation:      "bg-[#E8F3EC] text-[#1F5235] border-[#C8E0D1]",
    in_progress:                "bg-[#EDE5FA] text-[#4C1D95] border-[#D7C7F0]",
    completed:                  "bg-[#E8F3EC] text-[#1F5235] border-[#C8E0D1]",
};

const TAX_YEAR_OPTIONS = (() => {
    const now = new Date();
    const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    // Show the 2 past years + current + next
    const years = [];
    for (let y = year + 1; y >= year - 3; y--) years.push(y);
    return years.map((y) => ({ value: `${y}-03-31`, label: `31 March ${y}` }));
})();

function fmtTaxYear(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return `31 March ${d.getFullYear()}`;
    } catch { return iso; }
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

export default function RentalReminders() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterYear, setFilterYear] = useState("all");
    const [q, setQ] = useState("");
    const [clients, setClients] = useState([]);
    const [defaults, setDefaults] = useState({ checklist: [], statuses: STATUS_LABELS });
    const [creating, setCreating] = useState(false);
    const [bulkCreating, setBulkCreating] = useState(false);
    const [editing, setEditing] = useState(null); // reminder record

    async function loadAll() {
        setLoading(true);
        try {
            const [l, c, d] = await Promise.all([
                api.get("/rental-reminders"),
                api.get("/clients/"),
                api.get("/rental-reminders/defaults"),
            ]);
            setList(l.data || []);
            setClients(c.data || []);
            setDefaults(d.data || { checklist: [], statuses: STATUS_LABELS });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }

    useEffect(() => { loadAll(); }, []);

    const filtered = useMemo(() => {
        return list.filter((r) => {
            if (filterStatus !== "all" && r.status !== filterStatus) return false;
            if (filterYear !== "all" && r.tax_year_end !== filterYear) return false;
            if (q.trim()) {
                const s = q.trim().toLowerCase();
                if (!(r.client_name_snapshot || "").toLowerCase().includes(s)
                    && !(r.property_address || "").toLowerCase().includes(s)) return false;
            }
            return true;
        });
    }, [list, filterStatus, filterYear, q]);

    async function deleteReminder(rid) {
        try {
            await api.delete(`/rental-reminders/${rid}`);
            toast.success("Reminder deleted");
            await loadAll();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="space-y-5" data-testid="rental-reminders-page">
            <PageHeader
                title="Rental Property Reminders"
                subtitle="Annual rental tax-information reminder tool for existing clients."
                testId="rental-reminders-header"
            >
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setBulkCreating(true)}
                        data-testid="rr-bulk-btn"
                    >
                        <Users className="h-4 w-4" /> Bulk create
                    </Button>
                    <Button
                        onClick={() => setCreating(true)}
                        className="co-btn-primary"
                        data-testid="rr-new-btn"
                    >
                        <Plus className="h-4 w-4" /> New reminder
                    </Button>
                </div>
            </PageHeader>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="h-4 w-4 text-[#5E636E]" />
                    <Input placeholder="Search by client or property…" value={q}
                        onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="rr-search" />
                </div>
                <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="h-9 w-[180px]" data-testid="rr-filter-year">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All tax years</SelectItem>
                        {TAX_YEAR_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-9 w-[220px]" data-testid="rr-filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {Object.entries(STATUS_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="text-xs text-[#5E636E] ml-auto">
                    {filtered.length} of {list.length}
                </div>
            </div>

            {/* List */}
            <div className="co-card overflow-hidden">
                {loading ? (
                    <div className="p-6 flex items-center justify-center gap-2 text-sm text-[#5E636E]">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : list.length === 0 ? (
                    <div className="p-8 text-center">
                        <Home className="h-10 w-10 text-[#5E636E] mx-auto mb-3" />
                        <div className="text-sm font-medium">No rental reminders yet</div>
                        <div className="mt-1 text-xs text-[#5E636E] max-w-md mx-auto">
                            Start by creating one for an existing rental property client — pick the client, choose the tax year, and tick what records you need.
                        </div>
                        <Button onClick={() => setCreating(true)} className="mt-4 co-btn-primary">
                            <Plus className="h-4 w-4" /> Create reminder
                        </Button>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-6 text-sm text-[#5E636E] text-center">No reminders match these filters.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#FAFAF7] text-[11px] uppercase tracking-wide text-[#5E636E]">
                                <tr>
                                    <th className="text-left p-3">Client</th>
                                    <th className="text-left p-3">Tax year</th>
                                    <th className="text-left p-3">Property</th>
                                    <th className="text-left p-3">Status</th>
                                    <th className="text-left p-3">Reminders</th>
                                    <th className="text-left p-3">Last activity</th>
                                    <th className="text-right p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => (
                                    <tr key={r.id} className="border-t border-[#E4E4E7] hover:bg-[#FAFAF7]"
                                        data-testid={`rr-row-${r.id}`}>
                                        <td className="p-3">
                                            <div className="font-medium">{r.client_name_snapshot}</div>
                                            <div className="text-[11px] text-[#5E636E]">{r.client_email_snapshot || "—"}</div>
                                        </td>
                                        <td className="p-3 text-[#5E636E]">{fmtTaxYear(r.tax_year_end)}</td>
                                        <td className="p-3 text-[#5E636E]">{r.property_address || "—"}</td>
                                        <td className="p-3">
                                            <Badge variant="outline" className={`${STATUS_STYLES[r.status] || STATUS_STYLES.not_sent} border`}>
                                                {STATUS_LABELS[r.status] || r.status}
                                            </Badge>
                                        </td>
                                        <td className="p-3 text-[#5E636E] text-xs">
                                            {r.reminder_count || 0} sent
                                            {r.last_followup_at && (
                                                <div className="text-[10px]">Last follow-up {fmtDateTime(r.last_followup_at)}</div>
                                            )}
                                        </td>
                                        <td className="p-3 text-[#5E636E] text-xs">
                                            {r.updated_at ? fmtDateTime(r.updated_at) : "—"}
                                            <div className="text-[10px]">by {r.updated_by || "—"}</div>
                                        </td>
                                        <td className="p-3 text-right whitespace-nowrap">
                                            <Button size="sm" variant="outline" onClick={() => setEditing(r)}
                                                data-testid={`rr-open-${r.id}`}>
                                                <Pencil className="h-3.5 w-3.5" /> Open
                                            </Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button size="sm" variant="ghost" className="ml-1" title="Delete">
                                                        <Trash2 className="h-3.5 w-3.5 text-[#B91C1C]" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Delete this reminder?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This removes the rental reminder record. Sent emails stay in the log.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => deleteReminder(r.id)}>Delete</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {creating && (
                <CreateDialog
                    clients={clients}
                    defaultChecklist={defaults.checklist}
                    onClose={() => setCreating(false)}
                    onCreated={async (r) => {
                        setCreating(false);
                        await loadAll();
                        setEditing(r);
                    }}
                />
            )}

            {bulkCreating && (
                <BulkCreateDialog
                    clients={clients}
                    existing={list}
                    defaultChecklist={defaults.checklist}
                    onClose={() => setBulkCreating(false)}
                    onDone={async () => { setBulkCreating(false); await loadAll(); }}
                />
            )}

            {editing && (
                <EditDialog
                    reminder={editing}
                    onClose={() => setEditing(null)}
                    onChanged={async (fresh) => {
                        await loadAll();
                        setEditing(fresh);
                    }}
                    onDeleted={async () => { setEditing(null); await loadAll(); }}
                />
            )}
        </div>
    );
}

// -------- Create dialog ----------------------------------------------
function CreateDialog({ clients, defaultChecklist, onClose, onCreated }) {
    const [clientId, setClientId] = useState("");
    const [taxYear, setTaxYear] = useState(TAX_YEAR_OPTIONS[1]?.value || TAX_YEAR_OPTIONS[0]?.value);
    const [propertyAddress, setPropertyAddress] = useState("");
    const [entityType, setEntityType] = useState("");
    const [checklist, setChecklist] = useState(defaultChecklist.map((c) => ({ ...c, required: true, received: false })));
    const [customItem, setCustomItem] = useState("");
    const [customItems, setCustomItems] = useState([]);
    const [saving, setSaving] = useState(false);
    const [clientQ, setClientQ] = useState("");

    const client = clients.find((c) => c.id === clientId);

    useEffect(() => {
        if (client) {
            setPropertyAddress((p) => p || client.property_address || client.mailing_address || "");
            setEntityType((e) => e || client.tax_type || "");
        }
    }, [client]);

    const clientMatches = useMemo(() => {
        const s = clientQ.trim().toLowerCase();
        if (!s) return clients.slice(0, 50);
        return clients.filter((c) =>
            (c.name || "").toLowerCase().includes(s) ||
            (c.alias || "").toLowerCase().includes(s)
        ).slice(0, 50);
    }, [clientQ, clients]);

    function toggleItem(key) {
        setChecklist((cl) => cl.map((it) => it.key === key ? { ...it, required: !it.required } : it));
    }
    function addCustom() {
        const t = customItem.trim();
        if (!t) return;
        setCustomItems((arr) => [...arr, t]);
        setCustomItem("");
    }
    function removeCustom(i) {
        setCustomItems((arr) => arr.filter((_, idx) => idx !== i));
    }

    async function create() {
        if (!clientId) { toast.error("Pick a client"); return; }
        if (!taxYear) { toast.error("Pick a tax year"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/rental-reminders", {
                client_id: clientId,
                tax_year_end: taxYear,
                property_address: propertyAddress,
                entity_type: entityType,
                checklist,
                custom_items: customItems,
            });
            toast.success("Reminder created");
            await onCreated(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto" data-testid="rr-create-dialog">
                <DialogHeader>
                    <DialogTitle>New rental reminder</DialogTitle>
                    <DialogDescription>Pick the client, the tax year, and tick which records you'll need.</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Client */}
                    <div>
                        <div className="co-label mb-1">Client *</div>
                        {clientId ? (
                            <div className="rounded-md border border-[#E4E4E7] p-3 flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-medium">{client?.name}</div>
                                    <div className="text-[11px] text-[#5E636E]">
                                        {client?.contact_email || "no email on file"} · {client?.tax_type || "entity type —"}
                                    </div>
                                </div>
                                <Button size="sm" variant="ghost" onClick={() => setClientId("")}>Change</Button>
                            </div>
                        ) : (
                            <>
                                <Input placeholder="Search clients…" value={clientQ}
                                    onChange={(e) => setClientQ(e.target.value)}
                                    data-testid="rr-client-search" />
                                <div className="mt-2 max-h-[180px] overflow-y-auto rounded-md border border-[#E4E4E7] divide-y divide-[#F0EFEA]">
                                    {clientMatches.length === 0 && (
                                        <div className="p-3 text-xs text-[#5E636E]">No clients match.</div>
                                    )}
                                    {clientMatches.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => setClientId(c.id)}
                                            className="w-full text-left p-2 hover:bg-[#FAFAF7]"
                                            data-testid={`rr-client-${c.id}`}
                                        >
                                            <div className="text-sm font-medium">{c.name}</div>
                                            <div className="text-[11px] text-[#5E636E]">{c.contact_email || "—"} · {c.tax_type || "—"}</div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Tax Year */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="co-label mb-1">Tax year ended *</div>
                            <Select value={taxYear} onValueChange={setTaxYear}>
                                <SelectTrigger data-testid="rr-tax-year"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TAX_YEAR_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <div className="co-label mb-1">Entity type</div>
                            <Input value={entityType} onChange={(e) => setEntityType(e.target.value)}
                                placeholder="e.g. Individual, Trust" data-testid="rr-entity-type" />
                        </div>
                    </div>
                    <div>
                        <div className="co-label mb-1">Rental property address</div>
                        <Input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)}
                            placeholder="Optional — for your records" data-testid="rr-address" />
                    </div>

                    {/* Checklist */}
                    <div>
                        <div className="co-label mb-2">Information required</div>
                        <div className="space-y-1.5">
                            {checklist.map((it) => (
                                <label key={it.key} className="flex items-start gap-2 text-sm">
                                    <Checkbox
                                        checked={it.required}
                                        onCheckedChange={() => toggleItem(it.key)}
                                        data-testid={`rr-item-${it.key}`}
                                    />
                                    <span>{it.label}</span>
                                </label>
                            ))}
                        </div>

                        {customItems.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {customItems.map((t, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm">
                                        <span className="text-[#1F5235]">•</span>
                                        <span className="flex-1">{t}</span>
                                        <button type="button" onClick={() => removeCustom(i)}
                                            className="text-[11px] text-[#B91C1C]">Remove</button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="mt-2 flex items-center gap-2">
                            <Input value={customItem} onChange={(e) => setCustomItem(e.target.value)}
                                placeholder="Add custom item (optional)"
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                                data-testid="rr-custom-input" />
                            <Button type="button" variant="outline" onClick={addCustom}>Add</Button>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={create} disabled={saving || !clientId}
                        className="co-btn-primary" data-testid="rr-create-save">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create & review
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// -------- Bulk create dialog ----------------------------------------
function BulkCreateDialog({ clients, existing, defaultChecklist, onClose, onDone }) {
    const [taxYear, setTaxYear] = useState(TAX_YEAR_OPTIONS[1]?.value || TAX_YEAR_OPTIONS[0]?.value);
    const [picked, setPicked] = useState(() => new Set());
    const [clientQ, setClientQ] = useState("");
    const [checklist, setChecklist] = useState(defaultChecklist.map((c) => ({ ...c, required: true, received: false })));
    const [customItem, setCustomItem] = useState("");
    const [customItems, setCustomItems] = useState([]);
    const [saving, setSaving] = useState(false);

    const existingByYear = useMemo(() => {
        const m = new Set();
        existing.forEach((r) => {
            if (r.tax_year_end === taxYear) m.add(r.client_id);
        });
        return m;
    }, [existing, taxYear]);

    const matches = useMemo(() => {
        const s = clientQ.trim().toLowerCase();
        const base = !s
            ? clients
            : clients.filter((c) =>
                (c.name || "").toLowerCase().includes(s) ||
                (c.alias || "").toLowerCase().includes(s)
            );
        return base.slice(0, 300);
    }, [clientQ, clients]);

    function toggle(id) {
        setPicked((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    }
    function pickAllVisible() {
        setPicked((prev) => {
            const n = new Set(prev);
            matches.forEach((c) => {
                if (!existingByYear.has(c.id)) n.add(c.id);
            });
            return n;
        });
    }
    function clearAll() { setPicked(new Set()); }

    function toggleItem(key) {
        setChecklist((cl) => cl.map((it) => it.key === key ? { ...it, required: !it.required } : it));
    }
    function addCustom() {
        const t = customItem.trim();
        if (!t) return;
        setCustomItems((arr) => [...arr, t]);
        setCustomItem("");
    }
    function removeCustom(i) {
        setCustomItems((arr) => arr.filter((_, idx) => idx !== i));
    }

    async function create() {
        if (picked.size === 0) { toast.error("Pick at least one client"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/rental-reminders/bulk-create", {
                client_ids: Array.from(picked),
                tax_year_end: taxYear,
                checklist,
                custom_items: customItems,
            });
            const parts = [`${data.created} created`];
            if (data.skipped_existing) parts.push(`${data.skipped_existing} already existed`);
            if (data.skipped_missing) parts.push(`${data.skipped_missing} missing`);
            toast.success(parts.join(" · "));
            await onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    const toCreateCount = Array.from(picked).filter((id) => !existingByYear.has(id)).length;

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[860px] max-h-[92vh] overflow-y-auto" data-testid="rr-bulk-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Bulk create reminders
                    </DialogTitle>
                    <DialogDescription>
                        Pick several clients and spin up one reminder each at the same tax year.
                        Clients that already have a reminder for this year are skipped.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="co-label mb-1">Tax year ended *</div>
                            <Select value={taxYear} onValueChange={setTaxYear}>
                                <SelectTrigger data-testid="rr-bulk-year"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TAX_YEAR_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end justify-end gap-2 text-xs text-[#5E636E]">
                            <span className="font-medium text-[#0F1115]">{picked.size}</span> selected ·
                            <span className="font-medium text-[#1F5235]">{toCreateCount}</span> will create
                        </div>
                    </div>

                    {/* Client picker */}
                    <div>
                        <div className="co-label mb-1 flex items-center justify-between">
                            <span>Clients *</span>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={pickAllVisible}
                                    className="text-[11px] text-[#1E3A5F] hover:underline"
                                    data-testid="rr-bulk-select-visible">
                                    Select all visible
                                </button>
                                <button type="button" onClick={clearAll}
                                    className="text-[11px] text-[#B91C1C] hover:underline"
                                    data-testid="rr-bulk-clear">
                                    Clear
                                </button>
                            </div>
                        </div>
                        <Input
                            placeholder="Search clients…"
                            value={clientQ}
                            onChange={(e) => setClientQ(e.target.value)}
                            data-testid="rr-bulk-search"
                        />
                        <div className="mt-2 max-h-[280px] overflow-y-auto rounded-md border border-[#E4E4E7] divide-y divide-[#F0EFEA]">
                            {matches.length === 0 && (
                                <div className="p-3 text-xs text-[#5E636E]">No clients match.</div>
                            )}
                            {matches.map((c) => {
                                const alreadyHas = existingByYear.has(c.id);
                                const sel = picked.has(c.id);
                                return (
                                    <label
                                        key={c.id}
                                        className={`flex items-center gap-2 p-2 ${alreadyHas ? "opacity-50" : "hover:bg-[#FAFAF7] cursor-pointer"}`}
                                        data-testid={`rr-bulk-row-${c.id}`}
                                    >
                                        <Checkbox
                                            checked={sel}
                                            disabled={alreadyHas}
                                            onCheckedChange={() => !alreadyHas && toggle(c.id)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{c.name}</div>
                                            <div className="text-[11px] text-[#5E636E] truncate">
                                                {c.contact_email || "no email"} · {c.tax_type || "—"}
                                            </div>
                                        </div>
                                        {alreadyHas && (
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0EFEA] text-[#5E636E]">
                                                already exists
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* Shared checklist */}
                    <div>
                        <div className="co-label mb-2">Shared checklist (applied to each)</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {checklist.map((it) => (
                                <label key={it.key} className="flex items-start gap-2 text-sm">
                                    <Checkbox
                                        checked={it.required}
                                        onCheckedChange={() => toggleItem(it.key)}
                                        data-testid={`rr-bulk-item-${it.key}`}
                                    />
                                    <span>{it.label}</span>
                                </label>
                            ))}
                        </div>

                        {customItems.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {customItems.map((t, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm">
                                        <span className="text-[#1F5235]">•</span>
                                        <span className="flex-1">{t}</span>
                                        <button type="button" onClick={() => removeCustom(i)}
                                            className="text-[11px] text-[#B91C1C]">Remove</button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="mt-2 flex items-center gap-2">
                            <Input value={customItem} onChange={(e) => setCustomItem(e.target.value)}
                                placeholder="Add custom item (optional)"
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                                data-testid="rr-bulk-custom-input" />
                            <Button type="button" variant="outline" onClick={addCustom}>Add</Button>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={create}
                        disabled={saving || toCreateCount === 0}
                        className="co-btn-primary"
                        data-testid="rr-bulk-confirm"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Create {toCreateCount || ""} reminder{toCreateCount === 1 ? "" : "s"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// -------- Edit dialog ------------------------------------------------
function EditDialog({ reminder, onClose, onChanged }) {
    const [doc, setDoc] = useState(reminder);
    const [saving, setSaving] = useState(false);
    const [composing, setComposing] = useState(false);
    const [preview, setPreview] = useState(null); // {subject, body, to, follow_up, items}
    const [newCustom, setNewCustom] = useState("");

    useEffect(() => setDoc(reminder), [reminder]);

    async function patch(payload) {
        setSaving(true);
        try {
            const { data } = await api.patch(`/rental-reminders/${doc.id}`, payload);
            setDoc(data);
            await onChanged(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    function toggleRequired(key) {
        patch({
            checklist: doc.checklist.map((it) =>
                it.key === key ? { ...it, required: !it.required } : it
            ),
        });
    }
    function toggleReceived(key) {
        patch({
            checklist: doc.checklist.map((it) =>
                it.key === key ? { ...it, received: !it.received } : it
            ),
        });
    }
    function addCustom() {
        const t = newCustom.trim();
        if (!t) return;
        patch({ custom_items: [...(doc.custom_items || []), t] });
        setNewCustom("");
    }
    function removeCustom(i) {
        patch({ custom_items: doc.custom_items.filter((_, idx) => idx !== i) });
    }

    async function openCompose(followUp) {
        setComposing(true);
        try {
            const { data } = await api.post(`/rental-reminders/${doc.id}/compose`, { follow_up: followUp });
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setComposing(false); }
    }

    const required = (doc.checklist || []).filter((i) => i.required);
    const received = (doc.checklist || []).filter((i) => i.received);
    const outstanding = (doc.checklist || []).filter((i) => i.required && !i.received);

    return (
        <>
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[880px] max-h-[92vh] overflow-y-auto" data-testid="rr-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Home className="h-5 w-5" />
                        {doc.client_name_snapshot} — {fmtTaxYear(doc.tax_year_end)}
                    </DialogTitle>
                    <DialogDescription>
                        {doc.property_address || "No property address on file"} · Entity: {doc.entity_type || "—"}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* Left/center: checklist */}
                    <div className="md:col-span-2 space-y-5">
                        <section>
                            <div className="co-label mb-2 flex items-center justify-between">
                                <span>Checklist</span>
                                <span className="text-[11px] text-[#5E636E]">
                                    {received.length}/{required.length} received · {outstanding.length} outstanding
                                </span>
                            </div>
                            <div className="rounded-md border border-[#E4E4E7] divide-y divide-[#F0EFEA]">
                                {(doc.checklist || []).map((it) => (
                                    <div key={it.key} className="p-2.5 flex items-start gap-2">
                                        <Checkbox
                                            checked={it.required}
                                            onCheckedChange={() => toggleRequired(it.key)}
                                            data-testid={`rr-edit-req-${it.key}`}
                                        />
                                        <div className="flex-1 text-sm">
                                            <div className={it.required ? "" : "text-[#9CA3AF] line-through"}>{it.label}</div>
                                            {it.received && (
                                                <div className="text-[10px] text-[#1F5235]">Received {fmtDateTime(it.received_at)} by {it.received_by}</div>
                                            )}
                                        </div>
                                        {it.required && (
                                            <label className="flex items-center gap-1.5 text-[11px] text-[#5E636E]">
                                                <Checkbox
                                                    checked={!!it.received}
                                                    onCheckedChange={() => toggleReceived(it.key)}
                                                    data-testid={`rr-edit-rcv-${it.key}`}
                                                />
                                                received
                                            </label>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {(doc.custom_items || []).length > 0 && (
                                <ul className="mt-2 space-y-1 text-sm">
                                    {doc.custom_items.map((t, i) => (
                                        <li key={i} className="flex items-center gap-2">
                                            <span>•</span>
                                            <span className="flex-1">{t}</span>
                                            <button type="button" onClick={() => removeCustom(i)}
                                                className="text-[11px] text-[#B91C1C]">Remove</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                                <Input value={newCustom} onChange={(e) => setNewCustom(e.target.value)}
                                    placeholder="Add custom item"
                                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                                    data-testid="rr-edit-custom-input" />
                                <Button type="button" variant="outline" onClick={addCustom}>Add</Button>
                            </div>
                        </section>

                        <section>
                            <div className="co-label mb-1">Internal notes</div>
                            <Textarea
                                value={doc.internal_notes || ""}
                                onChange={(e) => setDoc({ ...doc, internal_notes: e.target.value })}
                                onBlur={() => patch({ internal_notes: doc.internal_notes || "" })}
                                rows={3}
                                data-testid="rr-notes"
                            />
                        </section>
                    </div>

                    {/* Right: status + actions */}
                    <div className="space-y-4">
                        <section className="rounded-md border border-[#E4E4E7] p-3 space-y-3">
                            <div>
                                <div className="co-label mb-1">Status</div>
                                <Select value={doc.status} onValueChange={(v) => patch({ status: v })}>
                                    <SelectTrigger data-testid="rr-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(STATUS_LABELS).map(([v, l]) => (
                                            <SelectItem key={v} value={v}>{l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="text-[11px] text-[#5E636E] space-y-1">
                                <div><strong className="text-[#0F1115]">To:</strong> {doc.client_email_snapshot || "—"}</div>
                                <div><strong className="text-[#0F1115]">Reminders sent:</strong> {doc.reminder_count || 0}</div>
                                {doc.reminder_sent_at && (
                                    <div><strong className="text-[#0F1115]">First sent:</strong> {fmtDateTime(doc.reminder_sent_at)} by {doc.reminder_sent_by}</div>
                                )}
                                {doc.last_followup_at && (
                                    <div><strong className="text-[#0F1115]">Last follow-up:</strong> {fmtDateTime(doc.last_followup_at)} by {doc.last_followup_by}</div>
                                )}
                            </div>
                        </section>

                        <div className="space-y-2">
                            <Button
                                className="co-btn-primary w-full"
                                onClick={() => openCompose(false)}
                                disabled={composing}
                                data-testid="rr-compose-initial"
                            >
                                {composing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                                {doc.reminder_count ? "Resend initial" : "Compose email"}
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => openCompose(true)}
                                disabled={composing || outstanding.length === 0}
                                data-testid="rr-compose-followup"
                                title={outstanding.length === 0 ? "Nothing outstanding to follow up on" : ""}
                            >
                                <Clock className="h-4 w-4" />
                                Follow-up ({outstanding.length} outstanding)
                            </Button>
                        </div>

                        {received.length === required.length && required.length > 0 && doc.status !== "completed" && (
                            <div className="rounded-md bg-[#E8F3EC] border border-[#C8E0D1] p-3 text-sm text-[#1F5235]">
                                All records received — ready for preparation.
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {preview && (
            <PreviewDialog
                reminderId={doc.id}
                preview={preview}
                onClose={() => setPreview(null)}
                onSent={async () => { setPreview(null); const { data } = await api.get(`/rental-reminders/${doc.id}`); setDoc(data); await onChanged(data); }}
            />
        )}
        </>
    );
}

// -------- Preview + Send dialog --------------------------------------
function PreviewDialog({ reminderId, preview, onClose, onSent }) {
    const [subject, setSubject] = useState(preview.subject);
    const [body, setBody] = useState(preview.body);
    const [to, setTo] = useState(preview.to || "");
    const [sending, setSending] = useState(false);

    async function send() {
        if (!to || !to.includes("@")) {
            toast.error("Enter a valid recipient email");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/rental-reminders/${reminderId}/send`, {
                subject, body, override_to: to, follow_up: preview.follow_up,
            });
            if (data.status === "sent") {
                toast.success("Email sent");
                await onSent();
            } else {
                toast.error(data.error || "Send failed");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto" data-testid="rr-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {preview.follow_up ? <Clock className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
                        {preview.follow_up ? "Follow-up preview" : "Email preview"}
                    </DialogTitle>
                    <DialogDescription>
                        Review and edit before sending — nothing is sent until you click Send.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <div className="co-label mb-1">To</div>
                        <Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="rr-preview-to" />
                    </div>
                    <div>
                        <div className="co-label mb-1">Subject</div>
                        <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="rr-preview-subject" />
                    </div>
                    <div>
                        <div className="co-label mb-1">Message</div>
                        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16}
                            className="font-mono text-[12.5px]" data-testid="rr-preview-body" />
                    </div>
                </div>

                <DialogFooter className="flex items-center">
                    <div className="text-[11px] text-[#5E636E] mr-auto flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" /> Sent via Outlook with firm branding
                    </div>
                    <Button variant="outline" onClick={onClose} disabled={sending}>Back</Button>
                    <Button onClick={send} disabled={sending} className="co-btn-primary" data-testid="rr-send-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Send
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
